/**
 * Welche Punkte die Hauptnavigation führt und wer sie sieht.
 *
 * Getrennt von `Shell.tsx`, weil das eine Regel ist und keine Darstellung:
 * Sie hängt an der Laufzeitkonfiguration und an der Rolle, nicht an React.
 * So lässt sie sich in der schnellen Kette prüfen statt erst im Browser —
 * `pnpm pruefen` läuft in Sekunden, `pnpm pruefen:ui` startet einen Browser.
 */

import type { Merkmale } from "@hwc/config";
import type { Rolle } from "@hwc/contracts";

export interface Navigationspunkt {
  readonly pfad: string;
  readonly text: string;
  /** Nur sichtbar, wenn dieses Merkmal im Kundenstack an ist (ADR-044). */
  readonly merkmal?: keyof Merkmale;
  /** Nur sichtbar für diese Rollen. Ohne Angabe: für alle. */
  readonly rollen?: readonly Rolle[];
  /**
   * Für diese Rollen **nicht** sichtbar, auch wenn sonst nichts dagegen
   * spricht.
   *
   * Zwei Felder statt einem, und der Unterschied ist nicht formal: `rollen`
   * beantwortet „wer darf das sehen" — sinnvoll, wo genau eine Rolle gemeint
   * ist (Administration). `verbotenFuer` beantwortet „wem ist das ausdrücklich
   * verwehrt" — sinnvoll, wo eine Rolle aus einem sonst offenen Bereich
   * ausgenommen ist. Mit nur `rollen` müsste dort jede andere Rolle
   * aufgezählt werden, und die Liste wäre ab der nächsten Rolle unvollständig.
   */
  readonly verbotenFuer?: readonly Rolle[];
}

/**
 * Wem `07` §7.2 einen Bereich ausdrücklich verwehrt.
 *
 * Der externe Partner ist typischerweise der Steuerberater: eigenes Konto,
 * „Leserecht auf einen abgegrenzten Bereich, etwa den Belegordner", und
 * **kein Zugriff auf Aufträge, Kommunikation, Wissen oder Personaldaten**.
 * Die drei ersten sind Navigationspunkte, und sie stehen deshalb hier.
 *
 * **Was er sehen darf, ist damit nicht beantwortet.** `07` §7.2 nennt einen
 * abgegrenzten Bereich als Beispiel, nicht als Liste. Ob dazu neben den
 * Dokumenten auch Dateien, Kunden, Kalender oder der Eingang gehören, gehört
 * zu `EXP-001` (Übergabe an Steuerberater und Branchensoftware, Phase 7) und
 * wird hier **nicht** vorweggenommen — eine Positivliste an dieser Stelle wäre
 * eine Entscheidung im Vorbeigehen.
 *
 * Bis dahin gilt das Belegte: Was das Dokument verbietet, verschwindet. Was es
 * offen lässt, bleibt sichtbar und ist beim Öffnen Sache des Quellsystems —
 * die ACL entscheidet dort und nicht hier (`01` §1.11).
 */
const PARTNER_VERBOTEN: readonly Rolle[] = ["external-partner"];

/** `07` §7.3 — Reihenfolge und Benennung stammen von dort, alle zwölf. */
export const NAVIGATION: readonly Navigationspunkt[] = [
  { pfad: "/", text: "Übersicht" },
  { pfad: "/eingang", text: "Eingang" },
  { pfad: "/auftraege", text: "Aufträge", verbotenFuer: PARTNER_VERBOTEN },
  { pfad: "/kunden", text: "Kunden" },
  { pfad: "/kalender", text: "Kalender" },
  { pfad: "/aufgaben", text: "Aufgaben" },
  { pfad: "/dateien", text: "Dateien" },
  { pfad: "/dokumente", text: "Dokumente" },
  { pfad: "/kommunikation", text: "Kommunikation", verbotenFuer: PARTNER_VERBOTEN },
  { pfad: "/wissen", text: "Wissen", verbotenFuer: PARTNER_VERBOTEN },
  // ADR-017 hält die KI vollständig hinter dem Piloten; der Stack startet mit
  // `features.ai: false`. Ein Menüpunkt, der dann ins Leere führt, wäre ein
  // Versprechen, das die Anlage nicht einlöst.
  { pfad: "/assistent", text: "Assistent", merkmal: "ai" },
  // „Administration nach Berechtigung" (`07` §7.3). Was dahinter liegt, führt
  // `07` §7.2 unter Inhaber/Administrator: Benutzer und Rechte, Betriebs- und
  // Sicherheitsstatus, Freigaben und Audit.
  { pfad: "/administration", text: "Administration", rollen: ["tenant-admin"] },
];

/**
 * Welche Punkte dieser Benutzer in diesem Stack sieht.
 *
 * **Das ist keine Berechtigungsprüfung.** Die entscheidet nach `01` §1.11 das
 * Quellsystem, und ein verstecktes Menü hat noch nie etwas geschützt. Was hier
 * passiert, ist eine Frage der Brauchbarkeit: Ein Punkt, den dieser Benutzer
 * nie öffnen kann, führt bei jedem Klick in dieselbe Absage.
 *
 * Die sichere Richtung ist deshalb bewusst gewählt: Ohne bekannte Rolle — etwa
 * bevor eine Anmeldung steht — bleibt ein rollengebundener Punkt **weg**. Er
 * erschiene sonst für jeden, und das wäre die Sorte Voreinstellung, die
 * niemandem auffällt, solange sie stimmt.
 */
export function sichtbareNavigation(
  merkmale: Partial<Merkmale>,
  rollen: readonly Rolle[],
): readonly Navigationspunkt[] {
  return NAVIGATION.filter((punkt) => {
    if (punkt.merkmal && merkmale[punkt.merkmal] !== true) return false;
    if (punkt.rollen && !punkt.rollen.some((r) => rollen.includes(r))) return false;
    if (punkt.verbotenFuer && punkt.verbotenFuer.some((r) => rollen.includes(r))) {
      return false;
    }
    return true;
  });
}

/** Der Name, unter dem die Shell auftritt. Auch der Rückfall für den Titel. */
export const MARKE = "Handwerker-Cloud";

/**
 * Der Titel des Dokuments zu einem Pfad.
 *
 * Bei clientseitigem Routing (ADR-043) lädt der Browser keine neue Seite und
 * setzt deshalb auch keinen neuen Titel. Ohne Zutun heißen **alle zwölf**
 * Ansichten gleich, und das kostet an drei Stellen, an denen es niemandem
 * sofort auffällt: im Verlauf, in den Lesezeichen und in der Tab-Leiste, wenn
 * jemand das Portal zweimal offen hat.
 *
 * Die Reihenfolge ist „Ansicht — Marke" und nicht umgekehrt: Ein Tab zeigt bei
 * wenig Platz den Anfang, und dort gehört das hin, was die Tabs voneinander
 * unterscheidet.
 */
export function seitentitel(
  pfad: string,
  punkte: readonly Navigationspunkt[] = NAVIGATION,
): string {
  const treffer = punkte.find((p) =>
    p.pfad === "/" ? pfad === "/" : pfad === p.pfad || pfad.startsWith(p.pfad + "/"));
  return treffer && treffer.pfad !== "/" ? `${treffer.text} — ${MARKE}` : MARKE;
}

/**
 * Was der globale „Neu"-Dialog anbietet — `07` §7.3.
 *
 * Der Satz dort lautet: „Ein globaler ‚Neu'-Dialog erstellt kontextbezogen
 * Auftrag, Aufgabe, Termin, Nachricht oder Dokumentverknüpfung."
 *
 * **Kontextbezogen kann er heute nicht.** Dafür müsste er wissen, welcher
 * Auftrag gerade offen ist, und Aufträge gibt es noch nicht. Was er kann, ist
 * der Weg dorthin, wo die Sache entsteht — und das steht in jedem Eintrag
 * ausdrücklich, statt eine Fähigkeit vorzutäuschen, die fehlt.
 *
 * Jeder Eintrag hängt an einem Navigationspunkt und erbt damit dessen
 * Sichtbarkeit: Ein Bereich, den dieser Benutzer nicht sieht, taucht auch hier
 * nicht auf. Ohne diese Kopplung wäre der Dialog die nächste Liste, die
 * driftet.
 */
export interface Neueintrag {
  readonly text: string;
  /** Pfad des Bereichs, in dem die Sache entsteht. */
  readonly pfad: string;
  /** Welches Paket sie baut — bis dahin führt der Eintrag zum Platzhalter. */
  readonly paket: string;
}

/*
 * **Kein Eintrag „Kunde", obwohl das Portal den Kundenstamm fuehrt.**
 *
 * `07` §7.3 zaehlt woertlich auf: „Auftrag, Aufgabe, Termin, Nachricht oder
 * Dokumentverknuepfung." Am 05.09.2026 stand hier kurz ein sechster Eintrag,
 * bis der Test daneben ihn zurueckgewiesen hat — zu Recht.
 *
 * Es ergibt auch so Sinn: Die fuenf hier entstehen aus einem Zusammenhang
 * heraus, den man gerade offen hat. Ein Kunde entsteht nicht nebenbei; er
 * wird in der Kundenliste angelegt, wo man vorher sieht, ob es ihn schon gibt.
 * Ein globaler Knopf waere der kuerzeste Weg zur zweiten Akte fuer denselben
 * Betrieb.
 */
export const NEU_EINTRAEGE: readonly Neueintrag[] = [
  { text: "Auftrag", pfad: "/auftraege", paket: "PORTAL-002" },
  { text: "Aufgabe", pfad: "/aufgaben", paket: "VIK-002" },
  { text: "Termin", pfad: "/kalender", paket: "NC-002" },
  { text: "Nachricht", pfad: "/kommunikation", paket: "MAIL-004" },
  { text: "Dokumentverknüpfung", pfad: "/dokumente", paket: "LNK-001" },
];

/** Die Einträge, deren Bereich dieser Benutzer in diesem Stack sieht. */
export function sichtbareNeueintraege(
  merkmale: Partial<Merkmale>,
  rollen: readonly Rolle[],
): readonly Neueintrag[] {
  const offen = new Set(sichtbareNavigation(merkmale, rollen).map((p) => p.pfad));
  return NEU_EINTRAEGE.filter((e) => offen.has(e.pfad));
}
